import { useCallback } from "react";
import config from "../config";

export interface EmbeddedSignupResult {
  code: string;
  wabaId: string;
  phoneNumberId: string;
}

interface SessionInfoData {
  waba_id?: string;
  phone_number_id?: string;
}

const FB_ORIGINS = [
  "https://www.facebook.com",
  "https://web.facebook.com",
] as const;

const isFBOrigin = (origin: string): boolean =>
  FB_ORIGINS.some((allowed) => origin === allowed);

const parseSessionInfo = (event: MessageEvent): SessionInfoData | null => {
  if (!isFBOrigin(event.origin)) {
    return null;
  }

  try {
    const data = JSON.parse(event.data);
    if (data.type !== "WA_EMBEDDED_SIGNUP") {
      return null;
    }
    return data.data as SessionInfoData;
  } catch {
    return null;
  }
};

export const useWhatsAppEmbeddedSignup = () => {
  const launchSignup = useCallback((): Promise<EmbeddedSignupResult> => {
    return new Promise((resolve, reject) => {
      if (typeof FB === "undefined") {
        reject(new Error("Facebook SDK not loaded"));
        return;
      }

      const configId = config.FACEBOOK.CONFIG_ID;
      if (!configId) {
        reject(new Error("Facebook Config ID not configured"));
        return;
      }

      let sessionInfo: SessionInfoData = {};

      const messageHandler = (event: MessageEvent) => {
        const parsed = parseSessionInfo(event);
        if (!parsed) {
          return;
        }
        sessionInfo = parsed;
      };

      window.addEventListener("message", messageHandler);

      FB.login(
        (response: FBLoginResponse) => {
          window.removeEventListener("message", messageHandler);

          if (!response.authResponse) {
            reject(
              new Error("User cancelled login or did not fully authorize."),
            );
            return;
          }

          resolve({
            code: response.authResponse.code,
            wabaId: sessionInfo.waba_id ?? "",
            phoneNumberId: sessionInfo.phone_number_id ?? "",
          });
        },
        {
          config_id: configId,
          response_type: "code",
          override_default_response_type: true,
          extras: {
            setup: {},
            featureType: "whatsapp_business_app_onboarding",
            sessionInfoVersion: "3",
          },
        },
      );
    });
  }, []);

  return { launchSignup };
};
